import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { queryClient } from "helpers/queryClient";

import type { SearchApiVersion } from "commonComponents/utilities/MainNavigation/AiSearch/aiSearchFlags";

import { useDebouncedValue } from "./useDebouncedValue";

/** One typeahead row: a record whose name the typed text could be. */
export interface ISearchSuggestion {
    text: string;
    entityType: string;
    id: string;
}

const MAX_SUGGESTIONS = 6;
const MIN_QUERY_LENGTH = 2;
const PAUSE_MS = 90;
/** How long an answer is reused for the same text. */
const CACHE_MS = 5 * 60_000;
/** Below this a correction is noise: two characters mistype into anything. */
const MIN_CORRECTION_LENGTH = 3;

/** What one answer from the suggest route says about one text. */
interface ISuggestAnswer {
    suggestions: ISearchSuggestion[];
    /** The spelling the service suggests instead, or null when it has none. */
    corrected: string | null;
}

type SuggestOutcome =
    | ({ kind: "suggestions" } & ISuggestAnswer)
    | { kind: "route-absent" }
    | { kind: "transient" };

/** Module scope: a `fetch` written inside `useEffect` is lint-banned. */
async function fetchSuggestions(input: {
    searchBaseUrl: string;
    searchApiVersion: SearchApiVersion;
    query: string;
    token: string;
    signal?: AbortSignal;
}): Promise<SuggestOutcome> {
    const response = await fetch(
        `${input.searchBaseUrl}${input.searchApiVersion}/search/suggest?q=${encodeURIComponent(
            input.query,
        )}`,
        {
            signal: input.signal,
            headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
        },
    );
    // Only a 404 says the route is not there. Any other answer that is not
    // JSON is an error page from something in between, such as a proxy or an
    // instance restarting, and says nothing about the next request.
    if (response.status === 404) {
        return { kind: "route-absent" };
    }
    const isJson = (response.headers.get("content-type") ?? "").includes("json");
    if (!response.ok || !isJson) {
        return { kind: "transient" };
    }
    const data = (await response.json()) as { suggestions?: unknown; corrected?: unknown };
    const value = (Array.isArray(data.suggestions) ? data.suggestions : [])
        .flatMap((s: unknown): ISearchSuggestion[] => {
            const row = s as { text?: unknown; entity_type?: unknown; id?: unknown };
            return typeof row.text === "string" && row.text.trim().length > 0
                ? [
                      {
                          text: row.text,
                          entityType: typeof row.entity_type === "string" ? row.entity_type : "",
                          id:
                              typeof row.id === "string" || typeof row.id === "number"
                                  ? String(row.id)
                                  : "",
                      },
                  ]
                : [];
        })
        .slice(0, MAX_SUGGESTIONS);
    const corrected =
        typeof data.corrected === "string" && data.corrected.trim().length > 0
            ? data.corrected.trim()
            : null;
    return { kind: "suggestions", suggestions: value, corrected };
}

/**
 * Names with a word that starts with what was typed first ("wym": Wymon,
 * Wymer, Wymbury), then the sound-alikes the service also finds (Whim, Whimm),
 * each group in the service's order.
 */
export function prefixFirst(suggestions: ISearchSuggestion[], query: string): ISearchSuggestion[] {
    const typed = query.trim().toLowerCase();
    const starts = (text: string) =>
        text.toLowerCase().startsWith(typed) ||
        (text.toLowerCase().match(/[\p{L}\p{N}][\p{L}\p{N}'-]*/gu) ?? []).some((w) =>
            w.startsWith(typed.split(/\s+/).pop() ?? typed),
        );
    return [
        ...suggestions.filter((s) => starts(s.text)),
        ...suggestions.filter((s) => !starts(s.text)),
    ];
}

export interface IUseSearchSuggestResult {
    suggestions: ISearchSuggestion[];
    /**
     * The service's did-you-mean for `forQuery`, or null. It comes in the same
     * answer as the names, so the dialog asks the suggest route once per text.
     */
    corrected: string | null;
    /** The query the suggestions on screen were fetched for. */
    forQuery: string;
}

/** One text's answer, as cached: what was asked and what came back. */
interface ICachedAnswer extends ISuggestAnswer {
    forQuery: string;
}

const NONE: ISuggestAnswer = { suggestions: [], corrected: null };

const queryKey = (searchBaseUrl: string, version: SearchApiVersion, text: string) =>
    ["searchSuggest", searchBaseUrl, version, text.toLowerCase()] as const;

/** Typeahead names from the search service's suggest route: debounced, cached per query, keeps the last list up while the next loads. */
export function useSearchSuggest(
    searchBaseUrl: string,
    query: string,
    getToken: () => Promise<string>,
    searchApiVersion: SearchApiVersion,
): IUseSearchSuggestResult {
    const [isRouteAbsent, setIsRouteAbsent] = useState(false);
    const trimmed = query.trim();
    const debounced = useDebouncedValue(trimmed, PAUSE_MS);
    const active = searchApiVersion === "v2" && searchBaseUrl.length > 0 && !isRouteAbsent;
    // A text answered before is read at once, without waiting out the pause.
    const asked =
        queryClient.getQueryData(queryKey(searchBaseUrl, searchApiVersion, trimmed)) !== undefined
            ? trimmed
            : debounced;

    const { data } = useQuery(
        queryKey(searchBaseUrl, searchApiVersion, asked),
        async ({ signal }): Promise<ICachedAnswer> => {
            const token = await getToken().catch(() => "");
            const outcome = await fetchSuggestions({
                searchBaseUrl,
                searchApiVersion,
                query: asked,
                token,
                signal,
            });
            if (outcome.kind === "route-absent") {
                setIsRouteAbsent(true);
                return { ...NONE, forQuery: asked };
            }
            if (outcome.kind === "transient") {
                // Not cached: the next keystroke asks again.
                throw new Error("suggest unavailable");
            }
            const key = asked.toLowerCase();
            return {
                forQuery: asked,
                suggestions: prefixFirst(outcome.suggestions, asked),
                corrected:
                    asked.length >= MIN_CORRECTION_LENGTH &&
                    outcome.corrected !== null &&
                    outcome.corrected.toLowerCase() !== key
                        ? outcome.corrected
                        : null,
            };
        },
        {
            enabled: active && asked.length >= MIN_QUERY_LENGTH,
            keepPreviousData: true,
            staleTime: CACHE_MS,
            cacheTime: CACHE_MS,
            retry: false,
            refetchOnWindowFocus: false,
        },
    );

    if (!active || trimmed.length < MIN_QUERY_LENGTH) {
        return { ...NONE, forQuery: trimmed };
    }
    return data ?? { ...NONE, forQuery: "" };
}
